import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '../../../services/logger.service';

import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CompanyService } from '../../../services/company.service';
import { StorefrontService } from '../../../services/storefront.service';
import { Subject, takeUntil, filter } from 'rxjs';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { StoreLocationsComponent } from './components/store-locations/store-locations.component';
import { COUNTRIES, getCountryName } from '../../../config/countries.config';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideImage } from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmTextareaImports } from '@spartan-ng/helm/textarea';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';

@Component({
  selector: 'app-storefront-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    SaveButtonComponent,
    StoreLocationsComponent,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmItemImports,
    HlmNativeSelectImports,
    HlmSwitchImports,
    HlmTextareaImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideImage,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './storefront-settings.component.html',
})
export class StorefrontSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  loading = false;
  storefrontForm: FormGroup;
  formChanged = false;
  businessAddressChanged = false;
  suburl: string | null = null;
  companyName: string = '';
  storefrontUrl: string = '';
  currentLogoPath: string | null = null;
  currentBannerPath: string | null = null;
  /** Further locations are for single shops; a network's places are its members. */
  isPlainShop = false;
  billingAddress: { street: string; city: string; zip: string; country: string } = {
    street: '',
    city: '',
    zip: '',
    country: '',
  };

  @ViewChild('basicInfoSaveButton') basicInfoSaveButton?: SaveButtonComponent;
  @ViewChild('activationSaveButton') activationSaveButton?: SaveButtonComponent;
  @ViewChild('businessAddressSaveButton') businessAddressSaveButton?: SaveButtonComponent;

  countries = COUNTRIES;

  constructor(
    private companyService: CompanyService,
    private storefrontService: StorefrontService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.suburl = this.companyService.getCurrentCompany()?.suburl || null;
    this.storefrontForm = this.fb.group({
      suburl: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9-]+$/)]],
      description: [''],
      is_storefront_enabled: [false],
      use_billing_as_business: [true],
      business_street: [''],
      business_city: [''],
      business_zip: [''],
      business_country: ['DE'],
    });

    this.storefrontForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formChanged = true;
      this.businessAddressChanged = true;
    });
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((company) => company !== null),
      )
      .subscribe((company) => {
        if (company) this.populateForm(company);
      });
  }

  private populateForm(company: any) {
    this.storefrontForm.patchValue(
      {
        suburl: company.suburl || '',
        description: company.description,
        is_storefront_enabled: company.is_storefront_enabled,
        use_billing_as_business: company.use_billing_as_business !== false,
        business_street: company.business_street || '',
        business_city: company.business_city || '',
        business_zip: company.business_zip || '',
        business_country: company.business_country || 'DE',
      },
      { emitEvent: false },
    );
    this.companyName = company.name;
    this.suburl = company.suburl;
    this.storefrontUrl = this.getStorefrontUrl();
    this.currentLogoPath = company.logo_image_path;
    this.currentBannerPath = company.banner_image_path;
    this.isPlainShop = !company.is_group_admin && !company.is_member;
    this.billingAddress = {
      street: company.billing_street || '',
      city: company.billing_city || '',
      zip: company.billing_zip || '',
      country: company.billing_country || 'DE',
    };
    this.formChanged = false;
    this.businessAddressChanged = false;
    this.setupBusinessAddressValidation();
  }

  private setupBusinessAddressValidation(): void {
    this.storefrontForm
      .get('use_billing_as_business')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((useBilling) => {
        const fields = ['business_street', 'business_city', 'business_zip'];
        fields.forEach((f) => {
          const ctrl = this.storefrontForm.get(f);
          if (ctrl) {
            if (useBilling) {
              ctrl.clearValidators();
              ctrl.setValue('');
            } else {
              ctrl.setValidators([Validators.required]);
            }
            ctrl.updateValueAndValidity();
          }
        });
      });

    if (!this.storefrontForm.get('use_billing_as_business')?.value) {
      ['business_street', 'business_city', 'business_zip'].forEach((f) => {
        const ctrl = this.storefrontForm.get(f);
        if (ctrl) {
          ctrl.setValidators([Validators.required]);
          ctrl.updateValueAndValidity();
        }
      });
    }
  }

  onSubmitBusinessAddress() {
    const useBilling = this.storefrontForm.get('use_billing_as_business')?.value;
    const fields = ['business_street', 'business_city', 'business_zip', 'business_country'];

    if (!useBilling) {
      const valid = fields.every((f) => this.storefrontForm.get(f)?.valid);
      if (!valid) {
        fields.forEach((f) => this.storefrontForm.get(f)?.markAsTouched());
        this.businessAddressSaveButton?.showErrorMessage(
          'Bitte fülle alle erforderlichen Felder aus.',
        );
        return;
      }
    }

    this.loading = true;
    this.businessAddressSaveButton?.hideMessages();

    const formData: any = {
      use_billing_as_business: useBilling,
      business_street: useBilling ? '' : this.storefrontForm.get('business_street')?.value,
      business_city: useBilling ? '' : this.storefrontForm.get('business_city')?.value,
      business_zip: useBilling ? '' : this.storefrontForm.get('business_zip')?.value,
      business_country: useBilling ? '' : this.storefrontForm.get('business_country')?.value,
    };

    this.companyService.updateCompanyDetails(formData).subscribe({
      next: () => {
        this.loading = false;
        this.businessAddressChanged = false;
        this.businessAddressSaveButton?.showSuccessMessage();
      },
      error: (error) => {
        const msg = error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving business address:', error);
        this.loading = false;
        this.businessAddressSaveButton?.showErrorMessage(msg);
      },
    });
  }

  /** Field errors show once the field has been touched. */
  isInvalid(controlName: string): boolean {
    const control = this.storefrontForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  onSubmitStorefrontDetails() {
    const invalids = ['suburl'].filter((f) => this.storefrontForm.get(f)?.invalid);
    if (invalids.length) {
      invalids.forEach((f) => this.storefrontForm.get(f)?.markAsTouched());
      return;
    }
    const suburl = this.storefrontForm.get('suburl')?.value;
    if (suburl)
      this.storefrontForm.patchValue({ suburl: suburl.toLowerCase() }, { emitEvent: false });
    this.saveStorefrontSettings(this.basicInfoSaveButton);
  }

  onSubmitActivationSettings() {
    this.saveStorefrontSettings(this.activationSaveButton);
  }

  private saveStorefrontSettings(saveButton?: SaveButtonComponent) {
    if (this.storefrontForm.valid && this.formChanged) {
      this.loading = true;
      saveButton?.hideMessages();
      this.companyService.updateCompanyDetails(this.storefrontForm.value).subscribe({
        next: () => {
          this.loading = false;
          this.formChanged = false;
          saveButton?.showSuccessMessage();
        },
        error: (error) => {
          const errorMessage =
            error.error?.detail || 'Fehler beim Aktualisieren. Bitte versuche es erneut.';
          this.logger.error('Error updating storefront settings:', error);
          this.loading = false;
          saveButton?.showErrorMessage(errorMessage);
        },
      });
    }
  }

  toggleStorefront() {
    const currentValue = this.storefrontForm.get('is_storefront_enabled')?.value;
    this.storefrontForm.patchValue({ is_storefront_enabled: !currentValue });
  }

  getCountryName(code: string): string {
    return getCountryName(code);
  }

  getStorefrontBaseDomain(): string {
    return this.storefrontService.getStorefrontBaseDomain();
  }

  getStorefrontUrl(): string {
    if (!this.suburl) return '#';
    const baseDomain = this.storefrontService.getStorefrontBaseDomain();
    const protocol = this.storefrontService.getProtocol();
    return `${protocol}${this.suburl}.${baseDomain}`;
  }

  getStorefrontDisplayUrl(): string {
    if (!this.suburl) return '';
    const baseDomain = this.storefrontService.getStorefrontBaseDomain();
    const protocol = this.storefrontService.getProtocol();
    return `${protocol}${this.suburl}.${baseDomain}`;
  }

  getImageUrl(imagePath: string): string {
    if (!imagePath) return '';
    const cleanPath = imagePath.startsWith('uploads/') ? imagePath.substring(8) : imagePath;
    return `${this.companyService.getApiBaseUrl()}/images/${cleanPath}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
